import { db } from '../firebase';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';

// Collection and document names
const SETTINGS_COLLECTION = 'settings';
const SETTINGS_DOC_ID = 'site_settings';

/**
 * Service for managing site-wide settings stored in Firestore
 */
const siteSettingsService = {
  /**
   * Initialize the settings collection and document if they don't exist
   * @returns {Promise<void>}
   */
  async initializeSettings() {
    try {
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsSnap = await getDoc(settingsRef);
      
      if (!settingsSnap.exists()) {
        // Create default settings
        const defaultSettings = {
          isProtected: true,
          password: 'dentalhero2025',
          useImmediatePaymentProcessing: false, // Default to authorization mode (not immediate)
          lastUpdated: new Date().toISOString()
        };
        
        await setDoc(settingsRef, defaultSettings);
        console.log('Site settings initialized with default values');
      }
    } catch (error) {
      console.error('Error initializing site settings:', error);
    }
  },
  
  /**
   * Get the current site settings
   * @returns {Promise<Object>} The site settings object
   */
  async getSettings() {
    try {
      // Ensure settings are initialized
      await this.initializeSettings();
      
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        return settingsSnap.data();
      } else {
        // This should not happen since we initialize settings above,
        // but just in case, return default settings
        return {
          isProtected: true,
          password: 'dentalhero2025',
          useImmediatePaymentProcessing: false,
          lastUpdated: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('Error getting site settings:', error);
      // Return default settings as fallback
      return {
        isProtected: true,
        password: 'dentalhero2025',
        useImmediatePaymentProcessing: false,
        lastUpdated: new Date().toISOString()
      };
    }
  },
  
  /**
   * Update the site settings
   * @param {Object} settings - The settings object to update
   * @returns {Promise<boolean>} Success status
   */
  async updateSettings(settings) {
    try {
      // Ensure settings are initialized
      await this.initializeSettings();
      
      const settingsRef = doc(db, SETTINGS_COLLECTION, SETTINGS_DOC_ID);
      
      // Add timestamp for when settings were last updated
      const updatedSettings = {
        ...settings,
        lastUpdated: new Date().toISOString()
      };
      
      await setDoc(settingsRef, updatedSettings);
      return true;
    } catch (error) {
      console.error('Error updating site settings:', error);
      return false;
    }
  },
  
  /**
   * Update the protection status
   * @param {boolean} isProtected - Whether the site should be password protected
   * @returns {Promise<boolean>} Success status
   */
  async updateProtectionStatus(isProtected) {
    try {
      const currentSettings = await this.getSettings();
      return await this.updateSettings({
        ...currentSettings,
        isProtected
      });
    } catch (error) {
      console.error('Error updating protection status:', error);
      return false;
    }
  },
  
  /**
   * Update the payment processing mode
   * @param {boolean} useImmediatePaymentProcessing - Whether to process payments immediately (true) or use authorization (false)
   * @returns {Promise<boolean>} Success status
   */
  async updatePaymentProcessingMode(useImmediatePaymentProcessing) {
    try {
      const currentSettings = await this.getSettings();
      return await this.updateSettings({
        ...currentSettings,
        useImmediatePaymentProcessing
      });
    } catch (error) {
      console.error('Error updating payment processing mode:', error);
      return false;
    }
  }
};

export default siteSettingsService;
